import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { RecaudacionReadDto } from '../models/mutuoRecaudar';
import { formatearRut } from '../../../utils/rutHelper';
import InputRut from '../../../components/inputs/yup/materialui/inputRut';
import InputSwitch from '../../../components/inputs/yup/materialui/inputSwitch';

import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import CloseIcon from '@mui/icons-material/Close';

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Grid2 from "@mui/material/Grid2";
import DialogActions from "@mui/material/DialogActions";
import { useToast } from '../../../hooks/useToast';
import InputText from '../../../components/inputs/yup/materialui/inputText';
import MButton from '../../../components/inputs/yup/materialui/mButton';
import InputFecha from '../../../components/inputs/yup/materialui/inputFecha';
import InputNumber from '../../../components/inputs/yup/materialui/inputNumber';
import { Typography } from '@mui/material';
import { CodigoParametro, SiNo, StatusCode, FormaPago } from '../../../utils/enums';
import { useRecaudacionIndividual } from '../hooks/useRecaudacionIndividual';
import { Recaudador } from '../models/recaudador';
import InputSelect from '../../../components/inputs/yup/materialui/inputSelect';
import { Parametro } from '../models/parametro';
import { selectorTransversal } from '../../../dtos/selector';
import { useDataFetch } from '../../../hooks/useDataFetch2';

interface IProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  onHide: () => void;
  mutuoRecaudacion: RecaudacionReadDto | null;
  onRecaudacionSuccess?: () => void;
}

interface RecaudacionForm {
  // Campos readonly
  mutuoId: number;
  numeroDividendo: number;
  emisor?: string;
  codigoMutuo?: string;
  omd?: string;
  numeroOperacion?: string;
  esAdministrado?: boolean;
  rutDeudor?: string;
  nombreDeudor?: string;

  // Campos editables
  procedenciaFondos: string;
  sucursalPago: string;
  fechaPago: Date;
  fechaVencimiento: Date; //readonly
  montoDividendo: number; //readonly
  montodividendobruto: number;
  interesMora: number;
  gastosCobranza: number;
  ajusteSencillo: number;
  totalRecaudado: number;
  formaPago: string;
  fechaRemesa?: Date;
  esRecaudacionInversionista: boolean;
  interespenaloriginal: number;
  gastoscobranzaoriginal: number;
  porcentajeBeneficio: number;
}

const ModalRecaudacion: React.FC<IProps> = (props) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isRecalculando, setIsRecalculando] = useState<boolean>(false);
  const [recaudadores, setRecaudadores] = useState<Recaudador[]>([]);
  const [formasPago, setFormasPago] = useState<selectorTransversal[]>([]);
  const [recaudadorDefault, setRecaudadorDefault] = useState<Parametro>();
  const { addToast } = useToast();
  const { obtenerRecaudadores, recalcularMontos, obtenerFormasPago, recaudarDividendo, contablizarOperacion } = useRecaudacionIndividual();

  const schemaRecaudacionForm = Yup.object<RecaudacionForm>().shape({
    mutuoId: Yup.number().required(),
    numeroDividendo: Yup.number().required(),
    emisor: Yup.string(),
    codigoMutuo: Yup.string(),
    omd: Yup.string(),
    numeroOperacion: Yup.string(),
    esAdministrado: Yup.boolean(),
    rutDeudor: Yup.string(),
    nombreDeudor: Yup.string(),
    procedenciaFondos: Yup.string().required("Campo requerido"),
    sucursalPago: Yup.string()
      .required("Campo requerido")
      .max(100, "La sucursal no puede tener más de 100 caracteres"),
    fechaPago: Yup.date().required("Campo requerido"),
    fechaVencimiento: Yup.date().required("Campo requerido"),
    montoDividendo: Yup.number().required("Campo requerido"),
    interesMora: Yup.number().required("Campo requerido")
      .default(0)
      .min(0, "El interés de mora no puede ser menor que 0")
      .test(
        'max-interes-original',
        function (value) {
          const original = this.parent.interespenaloriginal ?? 0;
          if ((value ?? 0) > original) {
            return this.createError({
              message: `El interés de mora no puede superar al interés mora calculado originalmente ($${original.toLocaleString('es-CL')})`
            });
          }
          return true;
        }
      ),
    gastosCobranza: Yup.number().required()
      .default(0)
      .min(0, "Los gastos de cobranza no pueden ser menores que 0")
      .test(
        'max-gastos-original',
        function (value) {
          const original = this.parent.gastoscobranzaoriginal ?? 0;
          if ((value ?? 0) > original) {
            return this.createError({
              message: `Los gastos de cobranza no pueden superar al gasto de cobranza calculado originalmente ($${original.toLocaleString('es-CL')})`
            });
          }
          return true;
        }
      ),
    ajusteSencillo: Yup.number().required("Campo requerido")
      .default(0),
    totalRecaudado: Yup.number().required("Campo requerido").min(0, "El total recaudado no puede ser menor que 0"),
    formaPago: Yup.string().required("Debe seleccionar una forma de pago"),
    fechaRemesa: Yup.date().when('esRecaudacionInversionista', {
      is: true,
      then: (schema) => schema.required("La fecha de remesa es requerida para recaudación inversionista"),
      otherwise: (schema) => schema.nullable()
    }),
    esRecaudacionInversionista: Yup.boolean().required(),
    interespenaloriginal: Yup.number().required(),
    gastoscobranzaoriginal: Yup.number().required(),
    montodividendobruto: Yup.number().required(),
    porcentajeBeneficio: Yup.number().required()
  });
  const {
    formState: { errors, isValid },
    handleSubmit,
    control,
    watch,
    reset,
    trigger,
    setValue,
    setError
  } = useForm<RecaudacionForm>({
    mode: "onTouched",
    resolver: yupResolver(schemaRecaudacionForm)
  });
  const dataForm = watch();

  useEffect(() => {
    const cargarRecaudadores = async () => {
      const data = await obtenerRecaudadores();
      if (data) {
        setRecaudadores(data);
      }
    };
    cargarRecaudadores();
  }, []);

  useEffect(() => {
    const cargarFormasPago = async () => {
      const data = await obtenerFormasPago();
      if (data) {
        setFormasPago(data);
      }
    };
    cargarFormasPago();
  }, []);

  useEffect(() => {
    if (props.mutuoRecaudacion != null && !isRecalculando) {
      const callRecalcularMontos = async () => {
        try {
          setIsRecalculando(true);
          const data = await recalcularMontos({
            mutuoid: props.mutuoRecaudacion!.id,
            numerodividendo: props.mutuoRecaudacion!.numerodividendo,
            fechapago: dataForm.fechaPago,
            rutrecaudador: dataForm.procedenciaFondos
          });

          if (data) {
            reset({
              ...dataForm,
              interesMora: data.interesPenal,
              gastosCobranza: data.gastosCobranza,
              montoDividendo: data.montoDividendo,
              montodividendobruto: data.montoDividendoBruto,
              porcentajeBeneficio: data.porcentajeBeneficio,
              interespenaloriginal: data.interesPenal,
              gastoscobranzaoriginal: data.gastosCobranza
            });
          } else {
            reset({
              ...dataForm,
              interesMora: 0,
              gastosCobranza: 0,
              montoDividendo: 0,
              montodividendobruto: 0,
              porcentajeBeneficio: 0,
              interespenaloriginal: 0,
              gastoscobranzaoriginal: 0
            });
            setError('fechaPago', { type: "onTouched", message: "No es posible recaudar para esta fecha" });
          }
        } catch (error) {
          console.error('Error al recalcular montos:', error);
          // setError('fechaPago', { type: "onTouched", message: "No es posible recaudar para esta fecha" });
        } finally {
          setIsRecalculando(false);
        }
      };
      callRecalcularMontos();
    }

  }, [dataForm.fechaPago, dataForm.procedenciaFondos, trigger]);

  useEffect(() => {
    if (props.show) {
      setTimeout(() => {
        trigger(); // Dispara la validación cuando se muestra el modal
      }, 1000);
    }
  }, [props.show, trigger]);

  useEffect(() => {
    if (dataForm.formaPago) {
      trigger('formaPago');
    }
  }, [dataForm.formaPago, trigger]);

  useEffect(() => {
    // Si se marca el check de recaudación inversionista, setear la fecha actual
    if (dataForm.esRecaudacionInversionista && !dataForm.fechaRemesa) {
      setValue('fechaRemesa', new Date());
    }
    // Si se desmarca el check de recaudación inversionista, limpiar la fecha de remesa
    else if (!dataForm.esRecaudacionInversionista && dataForm.fechaRemesa) {
      setValue('fechaRemesa', undefined as any);
    }
    trigger('fechaRemesa');
  }, [dataForm.esRecaudacionInversionista]);

  React.useEffect(() => {
    if (props.mutuoRecaudacion) {
      reset({
        ...dataForm,
        // Campos informativos del mutuo
        mutuoId: props.mutuoRecaudacion.id,
        numeroDividendo: props.mutuoRecaudacion.numerodividendo,
        emisor: props.mutuoRecaudacion.nombreEmisor,
        codigoMutuo: props.mutuoRecaudacion.codigomutuo || '',
        omd: props.mutuoRecaudacion.omd || '',
        numeroOperacion: props.mutuoRecaudacion.numerooperacion,
        esAdministrado: props.mutuoRecaudacion.esadministrado === SiNo.SI,
        rutDeudor: props.mutuoRecaudacion.rut,
        nombreDeudor: props.mutuoRecaudacion.nombre + ' ' + (props.mutuoRecaudacion.apellidopaternodeudor || '') + ' ' + (props.mutuoRecaudacion.apellidomaternodeudor || ''),

        // Campos editables de la recaudación 
        procedenciaFondos: props.mutuoRecaudacion.procedenciadefondos,
        sucursalPago: props.mutuoRecaudacion.sucursalpago || '',
        fechaPago: props.mutuoRecaudacion.fechapago,
        fechaVencimiento: props.mutuoRecaudacion.fechavencimiento, //readonly
        montoDividendo: props.mutuoRecaudacion.montodividendo, //readonly
        interesMora: props.mutuoRecaudacion.interespenal,
        gastosCobranza: props.mutuoRecaudacion.gastoscobranza,
        ajusteSencillo: 0,
        totalRecaudado: props.mutuoRecaudacion.montodividendo + props.mutuoRecaudacion.interespenal + props.mutuoRecaudacion.gastoscobranza,
        formaPago: FormaPago.Documento,
        fechaRemesa: props.mutuoRecaudacion.fecharemesa,
        esRecaudacionInversionista: false,
        interespenaloriginal: props.mutuoRecaudacion.interespenaloriginal,
        gastoscobranzaoriginal: props.mutuoRecaudacion.gastoscobranzaoriginal,
        montodividendobruto: props.mutuoRecaudacion.montodividendoBruto,
        porcentajeBeneficio: props.mutuoRecaudacion.porcentajeBeneficio
      });
    }
  }, [props.mutuoRecaudacion]);

  const onSubmit = async (data: RecaudacionForm) => {
    setIsSubmitting(true);

    try {
      const { emisor, codigoMutuo, omd, numeroOperacion, rutDeudor, esAdministrado, nombreDeudor, ...DataRecaudacionToSubmit } = data;

      // Recaudar dividendo
      await recaudarDividendo(DataRecaudacionToSubmit);

      // Feedback de recaudación exitosa
      addToast({
        mostrar: true,
        titulo: 'Confirmación',
        tipo: 'Success',
        mensaje: 'Dividendo recaudado correctamente'
      });

      setIsSubmitting(false);
      props.onHide();

      if (props.onRecaudacionSuccess) {
        props.onRecaudacionSuccess();
      }
    }
    catch (error) {
      console.error('Error en la recaudación:', error);
      setIsSubmitting(false);
    }
  };

  function customOnHide() {
    props.onHide()
  }

  return (
    <>

      <Dialog open={props.show} onClose={customOnHide} maxWidth="lg">
        <DialogTitle>Recaudar Dividendo</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* ——— Grupo 1: Datos del mutuo ——— */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Datos del mutuo
            </Typography>
            <Grid2 container spacing={2}>
              {/** Cada campo ocupa 12 cols en xs, 6 en sm, 4 en md, 3 en lg */}
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <InputText label="Emisor" name="emisor" control={control} error={errors.emisor} readonly />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <InputText label="Código mutuo" name="codigoMutuo" control={control} error={errors.codigoMutuo} readonly />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <InputText label="OMD" name="omd" control={control} error={errors.omd} readonly />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <InputText label="Número operación" name="numeroOperacion" control={control} error={errors.numeroOperacion} readonly />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <InputRut
                  label="RUT deudor"
                  name="rutDeudor"
                  control={control}
                  error={errors.rutDeudor}
                  readonly
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <InputText label="Nombre deudor" name="nombreDeudor" control={control} error={errors.nombreDeudor} readonly />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <InputText label="N° dividendo" name="numeroDividendo" control={control} error={errors.numeroDividendo} readonly />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <InputSwitch
                  label="Es administrado"
                  name="esAdministrado"
                  control={control}
                  error={errors.esAdministrado}
                  disabled
                />
              </Grid2>
            </Grid2>

            {/* ——— Grupo 2: Datos a ingresar ——— */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Datos a ingresar
            </Typography>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <InputSelect
                  label="Procedencia fondos"
                  name="procedenciaFondos"
                  control={control}
                  error={errors.procedenciaFondos}
                  clearable={false}
                  options={recaudadores.map(r => ({ value: r.rut, label: r.nombre }))}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <InputText
                  label="Sucursal pago"
                  name="sucursalPago"
                  control={control}
                  error={errors.sucursalPago} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <InputFecha
                  label="Fecha pago"
                  name="fechaPago"
                  fecha={dataForm.fechaPago}
                  control={control}
                  error={errors.fechaPago}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <InputFecha
                  label="Fecha vencimiento"
                  name="fechaVencimiento"
                  fecha={dataForm.fechaVencimiento}
                  control={control}
                  error={errors.fechaVencimiento}
                  esReadOnly
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <InputNumber
                  label='Monto dividendo'
                  name="montoDividendo"
                  control={control}
                  error={errors.montoDividendo}
                  prefix='$'
                  readonly
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <InputNumber
                  label="Interés mora"
                  name="interesMora"
                  control={control}
                  error={errors.interesMora}
                  prefix='$' />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <InputNumber
                  label="Gastos cobranza"
                  name="gastosCobranza"
                  control={control}
                  error={errors.gastosCobranza}
                  prefix='$' />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <InputNumber
                  label="Ajuste sencillo"
                  name="ajusteSencillo"
                  control={control}
                  error={errors.ajusteSencillo}
                  allowNegative={true}
                  prefix='$' />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <InputNumber
                  label="Total recaudado"
                  name="totalRecaudado"
                  control={control}
                  error={errors.totalRecaudado}
                  prefix='$' />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <InputSelect
                  label="Forma pago"
                  name="formaPago"
                  control={control}
                  error={errors.formaPago}
                  clearable={false}
                  options={formasPago.map(r => ({ value: r.value, label: r.label }))}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <InputFecha
                  label="Fecha remesa"
                  name="fechaRemesa"
                  fecha={dataForm.fechaRemesa}
                  control={control}
                  error={errors.fechaRemesa}
                  disabled={!dataForm.esRecaudacionInversionista}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <InputSwitch
                  label="Recaudación inversionista"
                  name="esRecaudacionInversionista"
                  control={control}
                  error={errors.esRecaudacionInversionista}
                />
              </Grid2>
            </Grid2>
          </form>
        </DialogContent>

        <DialogActions>
          <MButton
            variant="contained"
            color="primary"
            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}
            startIcon={<CheckCircleTwoToneIcon />}
          >
            Aceptar
          </MButton>
          <MButton
            variant="contained"
            color="inherit"
            onClick={() => {
              props.onHide();
              reset();
            }}
            startIcon={<CloseIcon />}
          >
            Cancelar
          </MButton>
        </DialogActions>
      </Dialog>

    </>
  )
}

export default ModalRecaudacion


